import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Tag } from 'antd';
import type { Conference } from '@viq-admin/types';
import { DataTable, type DataColumn } from '@viq-admin/ui';
import { ConferenceLink } from '../../../components/EntityLinks';
import { conferenceHref } from '../../../components/entityHrefs';
import { PHASE_COLOR, PHASE_LABEL, PHASE_RANK, conferencePhase, sortByPhase, todayISO } from './model';
import { useCompanyConferences } from './useCompanyHubData';

/**
 * The conferences this company has been granted (active ones; see
 * useCompanyConferences for what the backend returns). Rows arrive
 * live → upcoming → past and the table has no default sort, so that order
 * holds until the operator sorts a column. A row opens the conference.
 *
 * Uncontrolled table for the same reason as the Users tab: one route, several
 * tables, so no URL-synced table state.
 */
export function CompanyConferencesTab({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const { conferences, isLoading, error, refetch } = useCompanyConferences(companyId);
  const today = todayISO();
  const rows = useMemo(() => sortByPhase(conferences, today), [conferences, today]);

  const columns: DataColumn<Conference>[] = [
    {
      dataIndex: 'name',
      key: 'name',
      title: 'Name',
      kind: 'text',
      width: 260,
      render: (_: unknown, r: Conference) => <ConferenceLink id={r.id} name={r.name} />,
    },
    { dataIndex: 'location', key: 'location', title: 'Location', kind: 'text', width: 200 },
    { dataIndex: 'startDate', key: 'startDate', title: 'Starts', kind: 'date' },
    { dataIndex: 'endDate', key: 'endDate', title: 'Ends', kind: 'date' },
    {
      key: 'phase',
      title: 'When',
      kind: 'enum',
      width: 120,
      projectValue: (r) => PHASE_LABEL[conferencePhase(r, today)],
      sortable: (a, b) => PHASE_RANK[conferencePhase(a, today)] - PHASE_RANK[conferencePhase(b, today)],
      render: (_: unknown, r: Conference) => {
        const p = conferencePhase(r, today);
        return <Tag color={PHASE_COLOR[p]}>{PHASE_LABEL[p]}</Tag>;
      },
    },
  ];

  return (
    <DataTable<Conference>
      dataSource={rows}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      columns={columns}
      rowKey="id"
      storageKey="companyHubConferences"
      searchFields={['name', 'location']}
      searchPlaceholder="Search conferences"
      onRowClick={(r) => navigate(conferenceHref(r.id))}
      empty={{
        title: 'No conferences',
        description: 'Conferences this company is granted access to appear here.',
      }}
    />
  );
}
